import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@/contexts/UserContext';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { motion, AnimatePresence } from 'framer-motion';
import GradientAvatar from './GradientAvatar';

const SlideMenu = ({ isOpen, onClose, user, onLogout }) => {
  const navigate = useNavigate();
  const { isAdmin } = useUser();
  const { t } = useTranslation();
  const [imageError, setImageError] = useState(false);
  const [cachedImage, setCachedImage] = useState(null);

  useEffect(() => {
    if (user?.picture) {
      const cacheKey = `avatar_cache_${user.id || user.email}`;
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        setCachedImage(cached);
      } else {
        setCachedImage(user.picture);
      }
    }
  }, [user?.picture, user?.id, user?.email]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('menu-open');
    } else {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('menu-open');
    }
    return () => {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('menu-open');
    };
  }, [isOpen]);

  const menuItems = [
    { id: 'profile', icon: 'solar:user-linear', label: t('menu.profile'), path: '/profile' },
    { id: 'game', icon: 'solar:gamepad-linear', label: t('menu.game'), path: '/play' },
    { id: 'settings', icon: 'solar:settings-linear', label: t('menu.settings'), path: '/settings' },
    { id: 'subscription', icon: 'solar:card-linear', label: t('menu.subscription'), path: '/subscription' },
    { id: 'support', icon: 'solar:shield-linear', label: t('support.title'), path: '/support' },
  ];

  const bottomItems = [
    { id: 'privacy', icon: 'solar:shield-linear', label: t('menu.privacy'), path: '/privacy' },
    { id: 'terms', icon: 'solar:file-text-linear', label: t('menu.terms'), path: '/terms' },
  ];

  const handleNavigate = (path) => {
    navigate(path);
    onClose();
  };

  const handleLogout = () => {
    onLogout();
    onClose();
  };

  const handleImageLoad = () => {
    if (user?.picture) {
      const cacheKey = `avatar_cache_${user.id || user.email}`;
      try {
        localStorage.setItem(cacheKey, user.picture);
      } catch (e) {
        console.debug('Could not cache image:', e.message);
      }
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[9999]"
            style={{ backgroundColor: 'rgba(10,10,10,0.32)' }}
            onClick={onClose}
          />

          {/* Drawer */}
          <motion.div
            data-testid="slide-menu-drawer"
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              height: '100%',
              minHeight: '100dvh',
              width: 300,
              background: 'var(--c-surface)',
              zIndex: 10001,
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
              fontFamily: 'Manrope-Variable'
            }}
          >
            {/* Header */}
            <div data-testid="slide-menu-header" style={{ position: 'sticky', top: 0, zIndex: 10002, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '52px 24px 20px', background: 'var(--c-surface)' }}>
              <div style={{ fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--c-texSec)', fontWeight: 600 }}>{t('menu.title')}</div>
              <button onClick={onClose} style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', opacity: 0.5 }} aria-label={t('menu.close') || 'Close'}>
                <Icon icon="solar:close-square-linear" style={{ width: 16, height: 16, color: 'var(--c-texPri)' }} />
              </button>
            </div>

            {/* Profile card */}
            <div style={{ margin: '0 16px 12px', padding: 16, borderRadius: 16, background: 'var(--c-surface-card)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 9999, overflow: 'hidden', flexShrink: 0 }}>
                  {cachedImage && !imageError ? (
                    <img
                      src={cachedImage}
                      alt={user?.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onLoad={handleImageLoad}
                      onError={() => { setImageError(true); }}
                    />
                  ) : (
                    <GradientAvatar name={user?.name} email={user?.email} size={40} />
                  )}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--c-texPri)', lineHeight: 1.2 }}>{user?.name || t('menu.user')}</div>
                  <div style={{ fontSize: 12, color: 'var(--c-texSec)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.email || ''}</div>
                </div>
              </div>
            </div>

            {/* Primary menu */}
            <div style={{ margin: '0 16px 12px', borderRadius: 16, background: 'var(--c-surface-card)', overflow: 'hidden' }}>
              {menuItems.map((item) => (
                <div key={item.id} onClick={() => handleNavigate(item.path)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 16px', cursor: 'pointer' }}>
                  <Icon icon={item.icon} style={{ width: 18, height: 18, color: 'var(--c-texSec)', flexShrink: 0 }} />
                  <div style={{ flex: 1, fontSize: 15, color: 'var(--c-texPri)' }}>{item.label}</div>
                  <Icon icon="solar:alt-arrow-right-linear" style={{ width: 12, height: 12, color: 'var(--c-texTer)', opacity: 0.5 }} />
                </div>
              ))}
              {isAdmin && (
                <div onClick={() => handleNavigate('/admin')} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 16px', cursor: 'pointer' }}>
                  <Icon icon="solar:shield-keyhole-linear" style={{ width: 18, height: 18, color: 'var(--c-texSec)', flexShrink: 0 }} />
                  <div style={{ flex: 1, fontSize: 15, color: 'var(--c-texPri)' }}>Admin</div>
                  <Icon icon="solar:alt-arrow-right-linear" style={{ width: 12, height: 12, color: 'var(--c-texTer)', opacity: 0.5 }} />
                </div>
              )}
            </div>

            {/* Secondary links */}
            <div style={{ margin: '0 16px 12px', borderRadius: 16, background: 'var(--c-surface-card)', overflow: 'hidden' }}>
              {bottomItems.map((item) => (
                <div key={item.id} onClick={() => handleNavigate(item.path)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 16px', cursor: 'pointer' }}>
                  <Icon icon={item.icon} style={{ width: 16, height: 16, color: 'var(--c-texTer)', flexShrink: 0 }} />
                  <div style={{ flex: 1, fontSize: 13, color: 'var(--c-texSec)' }}>{item.label}</div>
                  <Icon icon="solar:alt-arrow-right-linear" style={{ width: 10, height: 10, color: 'var(--c-texTer)', opacity: 0.4 }} />
                </div>
              ))}
            </div>

            <div style={{ flex: 1 }} />

            {/* Logout + version */}
            <div style={{ padding: 16, paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 16px)' }}>
              <button data-testid="slide-menu-logout-btn" onClick={handleLogout} style={{ width: '100%', background: 'var(--c-surface-card)', color: 'var(--c-texPri)', border: '1px solid var(--c-border)', padding: 14, borderRadius: 12, fontSize: 13, letterSpacing: '0.12em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'Manrope-Variable', fontWeight: 600 }}>
                {t('menu.logout')}
              </button>
              <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--c-texTer)', marginTop: 12 }}>
                {(() => {
                  try {
                    // eslint-disable-next-line
                    const { GIT_VERSION } = require('../lib/version');
                    return `v${GIT_VERSION}`;
                  } catch {
                    return 'v0.0.0';
                  }
                })()}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SlideMenu;
